import { prefersReducedMotion } from '@theme/utilities';
import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';

/**
 * <carousel-loop> — seamless infinite scroll for Horizon resource-list product rails.
 *
 * Horizon's built-in `infinite` (carousel_loop) only produces a seamless wrap for
 * single-slide slideshows: the reorder trick in slideshow.js is gated to
 * `visibleSlides.length === 1`. A multi-card product rail is a native scroll-snap
 * container, so on touch (and trackpad/wheel) you simply reach the last card and stop.
 *
 * This component restores the "the same products keep scrolling forever" behaviour by
 * cloning the real slide set into leading + trailing buffers and snapping the native
 * scroll back by exactly one set-width whenever the position leaves the canonical zone
 * [home, home + setWidth). Because the buffers are pixel-identical to the real set, and
 * each jump target is exactly k set-widths from a real slide (so it lands on the same
 * scroll-snap point), the reposition is invisible — the rail loops in both directions.
 *
 * Wrapping is pure modular arithmetic on the measured set-width (no rounding to a
 * derived card pitch — that would accumulate drift across wraps). The jump only changes
 * `scroll-behavior` (never `scroll-snap-type`, which slideshow.js's Scroller co-owns),
 * so there is no snap-type race and no re-snap nudge at the seam.
 *
 * The reposition runs ONLY when the rail is at rest. Writing scrollLeft during an active
 * gesture cancels native trackpad/touch inertial scroll, so free scroll is left untouched
 * while moving — a wide clone buffer (several sets each side, see MIN_CLONE_COPIES) absorbs
 * the gesture, and a debounced settle handler does the ±set-width normalise once scrolling
 * fully stops (onto a pixel-identical clone, so it is invisible). A buffer that's too narrow
 * lets a hard fling/wheel-scroll outrun it and hit the literal end of the DOM — a real edge,
 * not a clone — where the browser just stops; the settle-triggered normalise then has to jump
 * back by a very visible multi-set distance instead of the intended imperceptible one.
 *
 * Arrows advance exactly one card. Before each step the position is normalised back into
 * the canonical zone, so a click can never drive the rail to the physical buffer edge
 * (where `scrollBy` clamps, emits no scroll event, and the loop would otherwise starve —
 * the "click does nothing until I scroll manually" bug).
 *
 * It wraps a <slideshow-component> rail but does NOT touch slideshow.js: clones have
 * their `ref`/`slide-id` stripped, so Horizon's slide logic (refs.slides, index sync,
 * pagination, IntersectionObserver) keeps operating on the real set only. Clones are
 * aria-hidden and removed from the tab order (links stay pointer-clickable), so assistive
 * tech and keyboard users only ever perceive the real set once.
 *
 * Known limitation: assumes a left-to-right (dir!=rtl) rail. The store ships no RTL
 * locale today (markets UK/US/AU/CA); see DECISIONS.md.
 */

/** Time (ms) of no scroll activity after which the rail is considered at rest. */
const SETTLE_MS = 120;

/**
 * Hard ceiling (ms) after which an arrow glide's #animating flag is force-cleared.
 * A glide is ~300ms; the settle handler normally clears the flag, but if the user keeps
 * scrolling it can keep re-arming the settle timer, so this guarantees the flag (and the
 * free-scroll wrap it gates) can never stick.
 */
const ANIMATE_MAX_MS = 600;

/** Min quiet time (ms) since the last user scroll before a resize is allowed to reposition. */
const RESIZE_QUIET_MS = 160;

/**
 * Minimum clone-set copies built on each side of the real set. This is the runway a single
 * free-scroll gesture (fast wheel-scroll, hard trackpad fling, scrollbar drag) can travel
 * before it outruns the buffer and hits the literal end of the DOM — a real edge, not a clone,
 * where the browser just stops and the eventual settle-triggered normalise has to jump back
 * a very visible multi-set distance instead of an imperceptible one. 2 copies (~2 set-widths
 * of runway) was reachable by an ordinary hard scroll; 4 gives comfortable headroom.
 */
const MIN_CLONE_COPIES = 4;

class CarouselLoop extends HTMLElement {
  /** @type {HTMLElement | null} The native scroll-snap rail. */
  #scroller = null;

  /** @type {HTMLElement | null} The inner slideshow component. */
  #slideshow = null;

  /** @type {HTMLElement[]} The original (non-clone) slides. */
  #realSlides = [];

  /** @type {HTMLElement | null} First real slide — measurement anchor. */
  #anchorReal = null;

  /** @type {HTMLElement | null} Its matching clone one full set later. */
  #anchorClone = null;

  /** @type {number} Scroll distance of one full real set, in px. */
  #setWidth = 0;

  /** @type {number} scrollLeft at which the real set begins. */
  #homeOffset = 0;

  /** @type {boolean} */
  #ready = false;

  /** @type {boolean} True while we are programmatically repositioning (instant jump). */
  #repositioning = false;

  /** @type {boolean} True while an arrow-driven smooth scroll is animating. */
  #animating = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #settleTimer = undefined;

  /** @type {ReturnType<typeof setTimeout> | undefined} Hard ceiling for #animating. */
  #animateCeiling = undefined;

  /** @type {number} performance.now() of the last user/momentum scroll (not our own jumps). */
  #lastScrollTs = 0;

  /**
   * The rail's intended resting scrollLeft, refreshed whenever it comes to rest. Used to undo
   * the scrollLeft perturbation a modal/drawer's scroll-lock inflicts on this snap rail.
   * @type {number}
   */
  #restingScrollLeft = 0;

  /** @type {MutationObserver | null} */
  #viewportObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    // Defer one frame so the inner <slideshow-component> has upgraded and laid out.
    requestAnimationFrame(() => this.#init());
  }

  disconnectedCallback() {
    this.#viewportObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('click', this.#onArrowClick, { capture: true });
    document.removeEventListener(DialogOpenEvent.eventName, this.#onDialogToggle, { capture: true });
    document.removeEventListener(DialogCloseEvent.eventName, this.#onDialogToggle, { capture: true });
    clearTimeout(this.#settleTimer);
    clearTimeout(this.#animateCeiling);
  }

  #init() {
    if (!this.isConnected) return;

    const scroller = this.querySelector('slideshow-slides');
    const slideshow = this.querySelector('slideshow-component');
    if (!(scroller instanceof HTMLElement) || !(slideshow instanceof HTMLElement)) return;

    this.#scroller = scroller;
    this.#slideshow = slideshow;
    this.#realSlides = this.#collectRealSlides();

    // Need at least two real slides to have something to loop (and to measure pitch).
    if (this.#realSlides.length < 2) return;

    // The rail only becomes scrollable once slideshow.js adds [in-viewport]
    // (off-screen rails are overflow:hidden to limit compositor layers). Wait for it.
    if (slideshow.hasAttribute('in-viewport')) {
      this.#activate();
    } else {
      this.#viewportObserver = new MutationObserver(() => {
        if (slideshow.hasAttribute('in-viewport')) this.#activate();
      });
      this.#viewportObserver.observe(slideshow, { attributes: true, attributeFilter: ['in-viewport'] });
    }
  }

  /** @returns {HTMLElement[]} */
  #collectRealSlides() {
    if (!this.#scroller) return [];
    return /** @type {HTMLElement[]} */ (
      Array.from(this.#scroller.children).filter(
        (el) => el.tagName === 'SLIDESHOW-SLIDE' && !el.hasAttribute('data-loop-clone')
      )
    );
  }

  #activate() {
    if (this.#ready || !this.#scroller) return;

    // Nothing to loop if the whole set already fits without scrolling.
    if (this.#scroller.scrollWidth <= this.#scroller.clientWidth + 1) return;

    this.#ready = true;
    this.#viewportObserver?.disconnect();

    this.#buildClones();
    this.#measure();
    this.#recenter(true);
    this.#restingScrollLeft = this.#scroller.scrollLeft;

    this.#scroller.addEventListener('scroll', this.#onScroll, { passive: true });

    // Intercept arrow clicks at window-capture (before Horizon's document-capture delegate)
    // so we can advance exactly one card at a time instead of a full page.
    window.addEventListener('click', this.#onArrowClick, { capture: true });

    // A modal/drawer opening or closing (cart drawer, quick-add, menu) toggles a
    // body{position:fixed} scroll-lock (dialog.js). That reflow re-snaps this
    // scroll-snap:x mandatory rail, perturbing its scrollLeft; left unchecked the perturbation
    // reads as a user scroll and #wrap teleports the rail onto a clone (the "carousel jumps to a
    // random position on add-to-cart" bug). DialogOpen/Close don't bubble, so listen at capture.
    document.addEventListener(DialogOpenEvent.eventName, this.#onDialogToggle, { capture: true });
    document.addEventListener(DialogCloseEvent.eventName, this.#onDialogToggle, { capture: true });

    // Card widths are container/viewport driven, so re-measure on resize.
    this.#resizeObserver = new ResizeObserver(() => this.#onResize());
    this.#resizeObserver.observe(this.#scroller);
  }

  #buildClones() {
    const scroller = this.#scroller;
    if (!scroller) return;

    const set = this.#realSlides;

    // At least MIN_CLONE_COPIES per side. The wrap now runs only at rest (see #onScroll), so a
    // single free-scroll gesture must be able to travel without reaching a physical buffer
    // edge before it settles — that runway needs to comfortably outlast a hard trackpad fling,
    // fast wheel-scroll, or scrollbar drag-to-end, not just "a normal" one. (Clones are
    // duplicates of already-fetched, lazy images, so the extra DOM is cheap.)
    const setExtent = set.reduce((sum, el) => sum + el.offsetWidth, 0);
    const copies = Math.max(MIN_CLONE_COPIES, Math.ceil(scroller.clientWidth / Math.max(setExtent, 1)));

    const leading = document.createDocumentFragment();
    const trailing = document.createDocumentFragment();
    let anchorClone = null;

    for (let copy = 0; copy < copies; copy++) {
      for (const slide of set) {
        const trailingClone = this.#makeClone(slide);
        // First trailing clone of the first real slide is our measurement anchor.
        if (copy === 0 && slide === set[0]) anchorClone = trailingClone;
        trailing.appendChild(trailingClone);

        leading.appendChild(this.#makeClone(slide));
      }
    }

    scroller.append(trailing);
    scroller.prepend(leading);

    // Post-connection sweep: custom-element connectedCallbacks fire during append/prepend
    // and may re-enable focus on descendants (e.g. by setting href or tabindex).
    // Re-run the defocus pass on all clones now that the DOM is live.
    for (const clone of scroller.querySelectorAll('[data-loop-clone]')) {
      for (const node of clone.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')) {
        node.setAttribute('tabindex', '-1');
      }
    }

    this.#anchorReal = set[0];
    this.#anchorClone = anchorClone;
  }

  /**
   * @param {HTMLElement} slide
   * @returns {HTMLElement}
   */
  #makeClone(slide) {
    const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
    clone.removeAttribute('ref'); // keep clone out of slideshow.js refs.slides
    clone.removeAttribute('slide-id'); // no duplicate slide ids
    clone.removeAttribute('data-offscreen');
    clone.setAttribute('data-loop-clone', '');
    clone.setAttribute('aria-hidden', 'true');

    // Remove clones from the tab order + accessibility tree, but leave links/buttons
    // pointer-clickable so a tap on a visible clone still works.
    for (const node of clone.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')) {
      node.setAttribute('tabindex', '-1');
    }

    // Strip `id` from the clone and every descendant (e.g. card-gallery's morph key, see
    // snippets/card-gallery.liquid). A raw cloneNode duplicates ids verbatim, and duplicate
    // ids on the page would confuse morph.js's id-based node matching on the next section
    // re-render, not just violate HTML uniqueness.
    if (clone.hasAttribute('id')) clone.removeAttribute('id');
    for (const node of clone.querySelectorAll('[id]')) {
      node.removeAttribute('id');
    }

    return clone;
  }

  #measure() {
    if (!this.#scroller || !this.#anchorReal || !this.#anchorClone) return;

    // Measure in viewport space so the result is independent of offsetParent/positioning.
    // The real-to-clone distance is invariant under scroll, so it is exactly one set-width.
    const railLeft = this.#scroller.getBoundingClientRect().left;
    const realLeft = this.#anchorReal.getBoundingClientRect().left;
    const cloneLeft = this.#anchorClone.getBoundingClientRect().left;

    // On page-width rails the scroller has scroll-padding-inline-start (the gutter), so the
    // mandatory-snap rest line sits that many px inward. Fold it into home once so every jump
    // lands exactly on the snap line (no post-jump snap correction → no seam ping-pong).
    // Zero on full-width rails, and a fixed offset either way, so modular arithmetic / zero
    // drift is preserved.
    const padStart = parseFloat(getComputedStyle(this.#scroller).scrollPaddingInlineStart) || 0;

    // Round to whole pixels for clean modular arithmetic. The rail's `scroll-snap-type:
    // x mandatory` pins any sub-pixel residual to the true snap point after each jump.
    this.#setWidth = Math.round(cloneLeft - realLeft);
    this.#homeOffset = Math.round(this.#scroller.scrollLeft + (realLeft - railLeft) - padStart);
  }

  /**
   * Live card pitch (width + gap) from real-slide geometry. Gap-inclusive and exact,
   * so the arrow step matches the layout's scroll-snap points at any breakpoint.
   * @returns {number}
   */
  #cardPitch() {
    const a = this.#realSlides[0];
    const b = this.#realSlides[1];
    if (!a || !b) return 0;
    return Math.round(b.getBoundingClientRect().left - a.getBoundingClientRect().left);
  }

  /**
   * Snap the scroll position back onto the real set without animation.
   * @param {boolean} [home] - When true, jump to the real set start.
   */
  #recenter(home = false) {
    if (!this.#scroller) return;
    const left = home ? this.#homeOffset : this.#scroller.scrollLeft;
    this.#jumpTo(left);
  }

  /**
   * Bring scrollLeft into the canonical zone [home, home + setWidth) by an instant
   * ±k·setWidth jump. Pixel-identical to the current view, so it is invisible.
   * @param {number} left
   * @returns {number} The number of set-widths jumped (0 if already in zone).
   */
  #normalizeFrom(left) {
    if (this.#setWidth <= 0) return 0;
    const k = Math.floor((left - this.#homeOffset) / this.#setWidth);
    if (k !== 0) this.#jumpTo(left - k * this.#setWidth);
    return k;
  }

  /**
   * Instantly set scrollLeft, defeating CSS smooth-scroll so the reposition is invisible.
   * Does NOT touch scroll-snap-type (slideshow.js's Scroller co-owns that inline property);
   * because the target is exactly k set-widths from a real slide it is itself a snap point,
   * so mandatory snap pins it with no nudge. Retries if the rail was briefly unscrollable
   * (it can be overflow:hidden during the [in-viewport] hand-off).
   * @param {number} left
   * @param {number} [attempt]
   */
  #jumpTo(left, attempt = 0) {
    const scroller = this.#scroller;
    if (!scroller) return;

    this.#repositioning = true;

    const prevBehavior = scroller.style.scrollBehavior;
    scroller.style.scrollBehavior = 'auto';
    scroller.scrollLeft = left;

    requestAnimationFrame(() => {
      scroller.style.scrollBehavior = prevBehavior;
      this.#repositioning = false;

      // If the rail was not yet scrollable, the assignment was ignored — retry.
      // Tolerance > 2px so a sub-pixel mandatory-snap correction is not mistaken for failure.
      if (attempt < 5 && Math.abs(scroller.scrollLeft - left) > 2) {
        this.#jumpTo(left, attempt + 1);
      }
    });
  }

  /** Normalise the current resting position into the canonical zone. */
  #wrap() {
    if (!this.#scroller || this.#setWidth <= 0) return;
    // While slideshow.js owns a pointer drag it sets scroll-snap-type:none and scrolls the
    // rail under the cursor; an instant jump now would teleport the content mid-drag. Defer:
    // the wrap runs on the post-release scroll settle instead.
    if (this.#slideshow?.hasAttribute('dragging')) return;
    this.#normalizeFrom(this.#scroller.scrollLeft);
  }

  /**
   * Advance exactly one card. Normalises into the zone first so the smooth animation can
   * never reach the physical buffer edge, then glides one pitch (or jumps instantly under
   * reduced motion). The per-event wrap is suppressed via #animating until the rail settles.
   * @param {1 | -1} direction
   */
  #step(direction) {
    if (!this.#ready || !this.#scroller || this.#setWidth <= 0) return;

    const pitch = this.#cardPitch();
    if (pitch <= 0) return;

    // Recentre before stepping: guarantees the projected target stays within the buffer.
    this.#normalizeFrom(this.#scroller.scrollLeft);

    const target = this.#scroller.scrollLeft + direction * pitch;

    if (prefersReducedMotion()) {
      // Instant step, landing on the in-zone equivalent of the target.
      const k = Math.floor((target - this.#homeOffset) / this.#setWidth);
      this.#jumpTo(target - k * this.#setWidth);
      return;
    }

    this.#animating = true;
    // Hard ceiling so #animating (and the free-scroll wrap it gates) can never stick on if
    // the user keeps scrolling and the settle timer never fires.
    clearTimeout(this.#animateCeiling);
    this.#animateCeiling = setTimeout(this.#onSettle, ANIMATE_MAX_MS);
    this.#scroller.scrollTo({ left: target, behavior: 'smooth' });
  }

  /**
   * Intercept prev/next arrow clicks before Horizon's document-capture delegate processes
   * them (which would page-jump by visibleSlides.length). Window-capture fires first.
   * @param {MouseEvent} e
   */
  #onArrowClick = (e) => {
    if (!this.#ready || !this.#scroller) return;
    const node = e.target;
    if (!(node instanceof Node) || !this.contains(node)) return;
    const el = node instanceof Element ? node : node.parentElement;
    const btn = el?.closest('[ref="next"], [ref="previous"]');
    if (!btn) return;

    e.stopPropagation();
    this.#step(btn.getAttribute('ref') === 'next' ? 1 : -1);
  };

  #onScroll = () => {
    if (!this.#ready || this.#repositioning || this.#setWidth <= 0) return;

    // Timestamp genuine user/momentum scrolls (our own jumps set #repositioning and bail
    // above) so #onResize can avoid yanking the rail mid-gesture.
    this.#lastScrollTs = performance.now();

    // Wrap ONLY at rest. Writing scrollLeft mid-gesture cancels native trackpad/touch
    // inertial scroll (the "stutter / can't slide a card" bug — worst when scrolling back
    // from the home rest position, which sits on the zone's lower edge). So we never
    // reposition during an active scroll: the wide clone buffer absorbs the gesture, and
    // this debounced settle normalises the position once the rail comes fully to rest
    // (a ±set-width jump onto a pixel-identical clone, so it is invisible).
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(this.#onSettle, SETTLE_MS);
  };

  #onSettle = () => {
    this.#animating = false;
    clearTimeout(this.#animateCeiling);
    this.#wrap();
    // Record where the rail came to rest, so a modal scroll-lock perturbation can be undone.
    if (this.#scroller) this.#restingScrollLeft = this.#scroller.scrollLeft;
  };

  /**
   * Undo the scrollLeft perturbation a modal/drawer scroll-lock inflicts on this rail.
   *
   * dialog.js sets body{position:fixed} on open and clears it on close; each reflow re-snaps this
   * scroll-snap:x mandatory rail off its resting position. DialogOpen/Close fire AFTER that reflow,
   * so we restore the last resting scrollLeft (captured in #onSettle) and cancel any pending settle
   * so #wrap never normalises the perturbed position onto a clone. Re-assert over the next two
   * frames to catch the lock's deferred application/release; the >1px guard makes it a no-op for
   * dialogs that don't move the rail.
   */
  #onDialogToggle = () => {
    if (!this.#ready || !this.#scroller || this.#setWidth <= 0) return;

    const anchor = this.#restingScrollLeft;
    clearTimeout(this.#settleTimer);

    const restore = () => {
      if (this.#scroller && Math.abs(this.#scroller.scrollLeft - anchor) > 1) this.#jumpTo(anchor);
    };

    restore(); // same frame as the lock change, before paint
    requestAnimationFrame(restore);
    requestAnimationFrame(() => requestAnimationFrame(restore));
  };

  #onResize() {
    if (!this.#ready || !this.#scroller || this.#setWidth <= 0) return;
    // Never reposition during an arrow glide, our own jump, or while the user is actively
    // scrolling (lazy-image decode / address-bar collapse fire ResizeObserver mid-gesture).
    if (this.#animating || this.#repositioning) return;
    if (performance.now() - this.#lastScrollTs < RESIZE_QUIET_MS) return;

    // Re-measure and only reposition if the set-width actually changed (a container-query
    // breakpoint). This skips the ResizeObserver's synthetic initial fire and any no-op
    // fire, both of which would otherwise jump the rail for no reason.
    const prevSetWidth = this.#setWidth;
    const prevFraction = (this.#scroller.scrollLeft - this.#homeOffset) / this.#setWidth;
    this.#measure();
    if (this.#setWidth <= 0 || this.#setWidth === prevSetWidth) return;
    const frac = (((prevFraction % 1) + 1) % 1);
    this.#restingScrollLeft = this.#homeOffset + frac * this.#setWidth;
    this.#jumpTo(this.#restingScrollLeft);
  }
}

if (!customElements.get('carousel-loop')) {
  customElements.define('carousel-loop', CarouselLoop);
}
